import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, LOGO_DARK } from '../config';
import { ArrowLeft, Flame, Activity, HeartPulse, Plus } from 'lucide-react';
import WeeklyConsistencyDots from './WeeklyConsistencyDots';
import GentleStepsCard from './GentleStepsCard';
import GraduationCard from './GraduationCard';
import TemperatureCheckCard from './TemperatureCheckCard';
import MidFlightCheck from './MidFlightCheck';
import BottomNavBar from './BottomNavBar';

const Overline = ({ children }) => (
  <p className="text-xs uppercase tracking-[0.2em] font-bold text-emerald-600">{children}</p>
);

const JourneyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isFitness = id?.startsWith('fitness_');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsIntervention, setNeedsIntervention] = useState(false);
  const [interventionJourney, setInterventionJourney] = useState(null);
  const [showLogMenu, setShowLogMenu] = useState(false);
  const [healthConnected, setHealthConnected] = useState(false);
  const [healthBusy, setHealthBusy] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}${isFitness ? `/fitness/detail/${id}` : `/journeys/${id}`}`);
      setData(res.data);
      if (!isFitness && res.data.status === 'active') {
        try {
          const st = await axios.get(`${API}/journey/check-status`);
          if (st.data.needs_intervention) {
            setNeedsIntervention(true);
            setInterventionJourney(st.data.journey);
          }
        } catch (e) { /* ignore */ }
      }
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) navigate('/', { replace: true });
      else navigate('/dashboard', { replace: true });
    }
  };

  useEffect(() => {
    setLoading(true);
    (async () => { await load(); })();
  }, [id]);

  const handleResume = async () => {
    try {
      await axios.post(`${API}/journeys/${id}/resume`);
      navigate('/dashboard');
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not resume this journey.');
    }
  };

  useEffect(() => {
    if (!isFitness) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/health/status`);
        setHealthConnected(!!res.data?.connected);
      } catch (e) { /* ignore */ }
    })();
  }, [isFitness]);

  const handleGoogleHealth = async () => {
    if (healthBusy) return;
    setHealthBusy(true);
    try {
      if (healthConnected) {
        await axios.post(`${API}/health/sync`);
        await load();
        alert('Synced your latest Google Health activity.');
      } else {
        const res = await axios.get(`${API}/health/google/connect`);
        if (res.data?.url) { window.location.href = res.data.url; return; }
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not connect to Google Health right now.');
    } finally {
      setHealthBusy(false);
    }
  };

  const handleGraduationResolve = (decision, resp) => {
    if (decision === 'evolve') {
      navigate('/dashboard', { state: { architect: 'evolve', previousJourneyId: id } });
    } else {
      navigate('/dashboard');
    }
  };

  const handleRescue = () => {
    navigate('/dashboard', { state: { architect: 'rescue' } });
  };

  const logWorkout = async (activity) => {
    try {
      const res = await axios.post(`${API}/fitness/log-activity`, { fitness_id: id, activity });
      setData((prev) => ({
        ...prev,
        current_streak: res.data.current_streak,
        longest_streak: res.data.longest_streak,
        week: res.data.week,
        planner: res.data.planner,
      }));
      setShowLogMenu(false);
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not log workout.');
    }
  };

  const fitnessToday = async (completed) => {
    try {
      await axios.post(`${API}/fitness/log`, { completed });
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Could not log activity.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600" />
      </div>
    );
  }

  const title = isFitness ? (data.goal_name || 'My Fitness Evolution') : (data.identity_goal || 'Your Journey');
  const streak = isFitness ? (data.current_streak || 0) : (data.streak || 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-24">
      {/* Header */}
      <header className="bg-white border-b border-neutral-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            data-testid="back-to-dashboard"
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-emerald-600 font-medium text-sm"
          >
            <ArrowLeft size={18} /> Dashboard
          </button>
          <img src={LOGO_DARK} alt="Daily Discipline" className="h-8 w-8" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 flex flex-col gap-8" data-testid="journey-detail-page">
        <div>
          <Overline>Journey Details</Overline>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900 mt-1 capitalize">{title}</h1>
        </div>

        {/* ===== PATH 1: HABIT ENGINE ===== */}
        <section data-testid="path-habit-engine" className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 sm:p-8 flex flex-col gap-6">
          <div className="flex items-center gap-2">
            <Activity size={18} className="text-emerald-600" />
            <h2 className="text-sm uppercase tracking-[0.15em] font-bold text-neutral-500">Habit Engine</h2>
          </div>

          <div className="flex flex-wrap items-center gap-x-10 gap-y-4">
            <div>
              <div className="flex items-center gap-2 text-orange-600">
                <Flame size={28} />
                <span className="text-4xl font-bold text-neutral-900">{streak}</span>
              </div>
              <p className="text-sm text-neutral-500 mt-1">Current streak</p>
            </div>
            {!isFitness && (
              <div>
                <span className="text-4xl font-bold text-neutral-900">{data.day_count}</span>
                <span className="text-xl text-neutral-400">/{data.target_days}</span>
                <p className="text-sm text-neutral-500 mt-1">Days on this journey</p>
              </div>
            )}
          </div>

          <div>
            <p className="text-xs text-neutral-400 uppercase tracking-wider mb-3">This week&apos;s consistency</p>
            <WeeklyConsistencyDots week={data.week || []} />
          </div>
        </section>

        {/* ===== HABIT: engagement (log / graduate / midpoint) ===== */}
        {!isFitness && data.status === 'active' && (
          <section className="flex flex-col gap-4">
            {data.is_graduation_ready ? (
              <GraduationCard journey={data} onResolve={handleGraduationResolve} />
            ) : (data.day_count >= 7 && data.day_count < 14 && !data.midpoint_feeling) ? (
              <TemperatureCheckCard journey={data} onCheckin={() => load()} onRescue={handleRescue} />
            ) : (
              <GentleStepsCard journey={data} streak={streak} onLog={() => load()} />
            )}
          </section>
        )}

        {!isFitness && data.status === 'paused' && (
          <section className="bg-white rounded-2xl border border-dashed border-neutral-300 p-6 text-center">
            <p className="text-neutral-600 mb-4">This journey is paused. Pick up right where you left off.</p>
            <button
              data-testid="detail-resume-button"
              onClick={handleResume}
              className="bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-full px-6 py-3 font-semibold transition-all"
            >
              Resume this journey
            </button>
          </section>
        )}

        {!isFitness && (data.status === 'completed' || data.status === 'graduated') && (
          <section className="bg-emerald-50 rounded-2xl border border-emerald-100 p-6 text-center">
            <p className="text-2xl mb-1">🎉</p>
            <h3 className="text-xl font-bold text-emerald-900">You mastered this journey</h3>
            <p className="text-emerald-700 mt-1">Longest streak: {data.longest_streak || 0} days. Beautifully done.</p>
          </section>
        )}

        {/* ===== FITNESS: today quick log ===== */}
        {isFitness && data.today && !data.today.is_rest_day && !data.today.logged && (
          <section className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 flex items-center justify-between gap-4">
            <div>
              <p className="text-xs text-neutral-400 uppercase tracking-wider capitalize">{data.today.day}&apos;s activity</p>
              <p className="text-lg font-semibold text-neutral-900">{data.today.activity}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => fitnessToday(true)} className="bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-full px-5 py-2.5 font-semibold text-sm transition-all">✓ Done</button>
              <button onClick={() => fitnessToday(false)} className="bg-neutral-100 hover:bg-neutral-200 text-neutral-600 rounded-full px-4 py-2.5 font-medium text-sm transition-all">Skip</button>
            </div>
          </section>
        )}

        {/* ===== PATH 2: EXERCISE PLANNER (fitness only) ===== */}
        {isFitness && (
          <section data-testid="path-exercise-planner" className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6 sm:p-8 flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <HeartPulse size={18} className="text-emerald-600" />
              <h2 className="text-sm uppercase tracking-[0.15em] font-bold text-neutral-500">Exercise Planner</h2>
            </div>
            <p className="text-sm text-neutral-500 -mt-3">Your selected weekly activities</p>

            <div className="flex flex-col gap-5">
              {(data.planner || []).map((act, idx) => (
                <div key={idx} data-testid={`planner-activity-${idx}`} className="border-b border-neutral-100 pb-5 last:border-0 last:pb-0">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-neutral-900">{act.name}</h3>
                    <span className="text-xs font-medium text-neutral-500">{act.slots_filled}/{act.per_week} this week</span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-3">
                    {act.slots.map((slot, si) =>
                      slot.done ? (
                        <span key={si} className="px-4 py-2 rounded-xl border-2 border-emerald-500 bg-emerald-50 text-emerald-700 font-bold text-sm flex items-center gap-2">
                          ✓ Done
                        </span>
                      ) : (
                        <button
                          key={si}
                          data-testid={`slot-${idx}-${si}`}
                          onClick={() => logWorkout(act.name)}
                          className="px-4 py-2 rounded-xl border-2 border-dashed border-neutral-300 text-neutral-500 font-medium text-sm bg-neutral-50 hover:border-emerald-400 hover:text-emerald-600 active:scale-95 transition-all flex items-center gap-2"
                        >
                          <Plus size={14} /> Slot {si + 1}
                        </button>
                      )
                    )}
                  </div>
                </div>
              ))}
              {(data.planner || []).length === 0 && (
                <p className="text-sm text-neutral-400">No activities scheduled yet.</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <button
                data-testid="sync-google-health"
                onClick={handleGoogleHealth}
                disabled={healthBusy}
                className="flex-1 bg-white border-2 border-emerald-200 text-emerald-700 rounded-full px-6 py-3 font-semibold hover:bg-emerald-50 active:scale-95 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {healthConnected ? 'Sync Google Fit' : 'Connect Google Fit'}
                <HeartPulse size={16} />
              </button>
              <div className="flex-1 relative">
                <button
                  data-testid="log-workout-button"
                  onClick={() => setShowLogMenu((s) => !s)}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white rounded-full px-6 py-3 font-semibold transition-all"
                >
                  Log Workout Manually
                </button>
                {showLogMenu && (
                  <div className="absolute z-10 mt-2 w-full bg-white border border-neutral-200 rounded-xl shadow-lg p-2" data-testid="log-workout-menu">
                    {(data.planner || []).map((act, idx) => (
                      <button
                        key={idx}
                        data-testid={`log-activity-${idx}`}
                        onClick={() => logWorkout(act.name)}
                        className="w-full text-left px-4 py-2.5 rounded-lg hover:bg-emerald-50 text-neutral-700 text-sm font-medium"
                      >
                        {act.name}
                      </button>
                    ))}
                    {(data.planner || []).length === 0 && (
                      <p className="px-4 py-2 text-sm text-neutral-400">No activities to log.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Journey info (habit) */}
        {!isFitness && (
          <section className="bg-white rounded-2xl border border-neutral-200 shadow-sm p-6">
            <h3 className="text-sm uppercase tracking-[0.15em] font-bold text-neutral-500 mb-4">The Contract</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between py-2 border-b border-neutral-100"><span className="text-neutral-500">Target Habit</span><span className="text-neutral-900 font-medium text-right ml-4">{data.target_habit}</span></div>
              <div className="flex justify-between py-2 border-b border-neutral-100"><span className="text-neutral-500">Tiny Win</span><span className="text-neutral-900 font-medium text-right ml-4">{data.tiny_win}</span></div>
              <div className="flex justify-between py-2"><span className="text-neutral-500">Anchor</span><span className="text-neutral-900 font-medium text-right ml-4">{data.anchor_habit}</span></div>
            </div>
          </section>
        )}
      </main>

      {needsIntervention && interventionJourney && (
        <MidFlightCheck
          journey={interventionJourney}
          onClose={() => setNeedsIntervention(false)}
          onAdjust={() => { setNeedsIntervention(false); load(); }}
        />
      )}

      <BottomNavBar currentPath="/dashboard" />
    </div>
  );
};

export default JourneyDetail;
